import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { store } from '../store';
import { setTokens, logout } from '../store/slices/authSlice';

function createApiClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = store.getState().auth.accessToken;
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor - handle token refresh
  let isRefreshing = false;
  let failedQueue: Array<{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void }> = [];

  const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = store.getState().auth.refreshToken;

        if (!refreshToken) {
          store.dispatch(logout());
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(
            `${import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:3001'}/auth/refresh-token`,
            { refreshToken },
          );

          const { accessToken, refreshToken: newRefreshToken } = response.data.data;
          store.dispatch(setTokens({ accessToken, refreshToken: newRefreshToken }));

          processQueue(null, accessToken);
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return client(originalRequest);
        } catch (refreshError: unknown) {
          processQueue(refreshError, null);
          // Only log the user out when the auth server explicitly rejects the token (4xx).
          // If there's no response (service still starting up / network down), keep the
          // user logged in so a cold `npm run dev` doesn't immediately clear the session.
          const httpStatus = (refreshError as { response?: { status?: number } })?.response?.status;
          if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
            store.dispatch(logout());
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );

  return client;
}

// Service-specific clients
export const userClient = createApiClient(
  import.meta.env.VITE_USER_SERVICE_URL || 'http://localhost:3001',
);

export const bookingClient = createApiClient(
  import.meta.env.VITE_BOOKING_SERVICE_URL || 'http://localhost:3002',
);

export const customsClient = createApiClient(
  import.meta.env.VITE_CUSTOMS_SERVICE_URL || 'https://custom-services-production.up.railway.app',
);

export const paymentClient = createApiClient(
  import.meta.env.VITE_PAYMENT_SERVICE_URL || 'http://localhost:3004',
);

export const trackingClient = createApiClient(
  import.meta.env.VITE_TRACKING_SERVICE_URL || 'http://localhost:3006',
);

export const partnerClient = createApiClient(
  import.meta.env.VITE_PARTNER_SERVICE_URL || 'http://localhost:3005',
);

export const analyticsClient = createApiClient(
  import.meta.env.VITE_ANALYTICS_SERVICE_URL || 'http://localhost:3008',
);

export const aiClient = createApiClient(
  import.meta.env.VITE_AI_SERVICE_URL || 'http://localhost:8001',
);

// Default export
export default userClient;
